/**
 * Marketplace Listing Management (tenant-side).
 *
 * Tenant owner / admin / manager controls visibility, content, and refreshes
 * the listing projection. The page is safe by design — visibility is OFF until
 * the owner explicitly opts in. Marketplace public discovery remains gated by
 * the `marketplace.enabled` platform flag.
 *
 * Route: /marketplace/listing
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Store, Eye, EyeOff, RefreshCw, MapPin, Layers, Save, Tags,
  Settings as Cog, Globe, CheckCircle2, Image as ImageIcon, Plus, X as XIcon,
} from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { API_BASE } from "@/lib/api";

const Toggle = ({ on, onChange, label, sub, testId }) => (
  <label className="flex items-start justify-between gap-3 p-3 rounded-lg border border-stone-200 hover:bg-stone-50 cursor-pointer" data-testid={testId}>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-stone-800">{label}</div>
      {sub && <div className="text-[11px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition ${on ? "bg-[#7A1F3D]" : "bg-stone-300"}`}
      aria-pressed={on}
    >
      <span className={`inline-block h-4 w-4 bg-white rounded-full shadow transform transition ${on ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  </label>
);

export default function MarketplaceListing() {
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [content, setContent] = useState({ name_en: "", name_ar: "", about_en: "", about_ar: "", tags: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketplace/listing/me");
      setListing(r.data);
      setContent({
        name_en: r.data.name?.en || "",
        name_ar: r.data.name?.ar || "",
        about_en: r.data.about?.en || "",
        about_ar: r.data.about?.ar || "",
        tags: (r.data.tags || []).join(", "),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تحميل بطاقة المنصة");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patchVisibility = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put("/marketplace/listing/me/visibility", patch);
      setListing(r.data);
      toast.success("تم الحفظ");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally {
      setBusy(false);
    }
  };

  const saveContent = async () => {
    setBusy(true);
    try {
      const r = await api.put("/marketplace/listing/me/content", {
        name: { en: content.name_en, ar: content.name_ar },
        about: { en: content.about_en, ar: content.about_ar },
        tags: content.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setListing(r.data);
      toast.success("تم تحديث المحتوى");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      await api.post("/marketplace/listing/me/refresh");
      await load();
      toast.success("تم تحديث الإسقاط من بيانات الصالون");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التحديث");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-stone-500">جاري التحميل…</div>;
  if (!listing) return <div className="p-8 text-center text-stone-500">لا توجد بطاقة بعد.</div>;

  const v = listing.visibility || {};
  const live = !!v.in_marketplace;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="marketplace-listing-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-stone-500">
            <Globe size={14} className="text-[#7A1F3D]" /> Marketplace
          </div>
          <h1 className="text-2xl font-bold text-stone-800 mt-1">بطاقة منصة iSaloon</h1>
          <p className="text-sm text-stone-500 mt-1">
            تحكّم في ظهور صالونك في منصة الاكتشاف العامة. التحكم لك بالكامل — اعرض أو أخفِ أي بيانات.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="marketplace-refresh-projection" disabled={busy} onClick={refresh} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-200 hover:border-[#7A1F3D] text-sm bg-white">
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> تحديث الإسقاط من الصالون
          </button>
        </div>
      </div>

      {/* Live badge */}
      <div className={`rounded-xl p-4 mb-4 flex items-center justify-between gap-3 ${live ? "bg-emerald-50 border border-emerald-200" : "bg-stone-50 border border-stone-200"}`} data-testid="marketplace-live-status">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${live ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
            {live ? <Eye size={18} /> : <EyeOff size={18} />}
          </div>
          <div>
            <div className="font-semibold text-stone-800">{live ? "ظاهر على المنصة" : "غير ظاهر"}</div>
            <div className="text-xs text-stone-500">
              {live ? "يمكن للعملاء العثور عليك في الـ Marketplace العام." : "لن يظهر صالونك للعموم حتى تُفعّل الظهور."}
            </div>
          </div>
        </div>
        <code className="text-[11px] text-stone-500 hidden sm:block">slug: {listing.slug}</code>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Visibility */}
        <div className="bg-white rounded-xl border border-stone-200">
          <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2 bg-stone-50/50">
            <Cog size={16} className="text-[#7A1F3D]" />
            <span className="font-semibold text-stone-700">إعدادات الظهور</span>
          </div>
          <div className="p-4 space-y-2">
            <Toggle testId="vis-in-marketplace" on={!!v.in_marketplace} onChange={(on) => patchVisibility({ in_marketplace: on })}
              label="عرض على المنصة"
              sub="الزر الرئيسي. عند الإطفاء لا يُظهر شيء على Marketplace." />
            <Toggle testId="vis-show-prices" on={!!v.show_prices} onChange={(on) => patchVisibility({ show_prices: on })}
              label="عرض الأسعار للعموم"
              sub="إن أُطفئ، تظهر الخدمات بدون أسعار للزوّار." />
            <Toggle testId="vis-show-staff" on={!!v.show_staff} onChange={(on) => patchVisibility({ show_staff: on })}
              label="عرض الموظفين والمختصين"
              sub="عرض أسماء/صور الفريق في صفحة الصالون." />
            <Toggle testId="vis-online-booking" on={!!v.online_booking_enabled} onChange={(on) => patchVisibility({ online_booking_enabled: on })}
              label="السماح بالحجز الأونلاين"
              sub="عند الإطفاء، صفحتك للعرض فقط بدون حجز فوري." />
            <Toggle testId="vis-instant-book" on={!!v.instant_book} onChange={(on) => patchVisibility({ instant_book: on })}
              label="حجز فوري بدون تأكيد"
              sub="موصى به للصالونات ذات السعة المرنة." />
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl border border-stone-200">
          <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2 bg-stone-50/50">
            <Tags size={16} className="text-[#7A1F3D]" />
            <span className="font-semibold text-stone-700">المحتوى</span>
          </div>
          <div className="p-4 space-y-3 text-sm">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-stone-500 mb-1">الاسم (عربي)</label>
                <input data-testid="content-name-ar" value={content.name_ar} onChange={(e) => setContent({...content, name_ar: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none" />
              </div>
              <div>
                <label className="block text-xs text-stone-500 mb-1">Name (English)</label>
                <input data-testid="content-name-en" value={content.name_en} onChange={(e) => setContent({...content, name_en: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none" dir="ltr" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">نبذة (عربي)</label>
              <textarea data-testid="content-about-ar" rows={3} value={content.about_ar} onChange={(e) => setContent({...content, about_ar: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none resize-none" />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">About (English)</label>
              <textarea data-testid="content-about-en" rows={3} value={content.about_en} onChange={(e) => setContent({...content, about_en: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none resize-none" dir="ltr" />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">Tags (مفصولة بفواصل)</label>
              <input data-testid="content-tags" value={content.tags} onChange={(e) => setContent({...content, tags: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none" dir="ltr" placeholder="bridal, halal, ladies-only" />
            </div>
            <button data-testid="content-save" disabled={busy} onClick={saveContent} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white text-sm">
              <Save size={14} /> حفظ المحتوى
            </button>
          </div>
        </div>
      </div>

      {/* Media — cover/logo/gallery */}
      <MediaSection listing={listing} onSaved={load} />

      {/* Snapshots */}
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-2 text-stone-700 font-semibold mb-3">
            <MapPin size={16} className="text-[#7A1F3D]" /> الفروع المُسقطة
          </div>
          {(listing.branches || []).length === 0 ? (
            <div className="text-sm text-stone-500">لا توجد فروع. أضف فروعاً ثم اضغط «تحديث الإسقاط».</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {listing.branches.map((b, i) => (
                <BranchGeoRow key={i} b={b} idx={i} onSaved={load} />
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-2 text-stone-700 font-semibold mb-3">
            <Layers size={16} className="text-[#7A1F3D]" /> المعلومات المُسقطة
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-stone-50 rounded-lg p-3">
              <div className="text-[10px] uppercase text-stone-500">Vertical</div>
              <div className="font-semibold text-stone-800 mt-1">{listing.vertical}</div>
            </div>
            <div className="bg-stone-50 rounded-lg p-3">
              <div className="text-[10px] uppercase text-stone-500">Status</div>
              <div className="font-semibold text-stone-800 mt-1">{listing.status}</div>
            </div>
            <div className="bg-stone-50 rounded-lg p-3">
              <div className="text-[10px] uppercase text-stone-500">Price range</div>
              <div className="font-semibold text-stone-800 mt-1">
                {(listing.price_range?.min ?? 0)} – {(listing.price_range?.max ?? 0)} {listing.price_range?.currency || "QAR"}
              </div>
            </div>
            <div className="bg-stone-50 rounded-lg p-3">
              <div className="text-[10px] uppercase text-stone-500">Rating</div>
              <div className="font-semibold text-stone-800 mt-1">
                {listing.rating?.avg ?? 0} ({listing.rating?.count ?? 0})
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const BranchGeoRow = ({ b, idx, onSaved }) => {
  const [lat, setLat] = useState(b.geo?.coordinates?.[1] ?? "");
  const [lng, setLng] = useState(b.geo?.coordinates?.[0] ?? "");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const [inMp, setInMp] = useState(b.in_marketplace ?? false);

  useEffect(() => { setInMp(b.in_marketplace ?? false); }, [b.in_marketplace]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("المتصفح لا يدعم تحديد الموقع"); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLat(pos.coords.latitude.toFixed(6)); setLng(pos.coords.longitude.toFixed(6)); setBusy(false); setEdit(true); },
      (err) => { toast.error("تعذّر تحديد الموقع: " + err.message); setBusy(false); },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const save = async () => {
    if (!b.branch_id) { toast.error("الفرع بدون branch_id"); return; }
    if (lat === "" || lng === "") { toast.error("أدخل lat و lng"); return; }
    setBusy(true);
    try {
      await api.put("/marketplace/listing/me/branch-geo", {
        branch_id: b.branch_id, lat: Number(lat), lng: Number(lng),
      });
      toast.success("تم حفظ الموقع");
      setEdit(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setBusy(false); }
  };

  const toggleMarketplace = async (next) => {
    setInMp(next);
    setBusy(true);
    try {
      await api.put(`/marketplace/listing/me/branches/${b.branch_id}/visibility`, { in_marketplace: next });
      toast.success(next ? "تم عرض الفرع في الماركت بليس ✓" : "تم إخفاء الفرع من الماركت بليس");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التحديث");
      setInMp(!next);
    } finally { setBusy(false); }
  };

  const branchName = (typeof b.name === "object" ? (b.name?.ar || b.name?.en) : b.name) || `Branch ${idx + 1}`;

  return (
    <li className="p-3 rounded-lg bg-stone-50 border border-stone-100" data-testid={`branch-geo-row-${idx}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-bold text-stone-800 truncate">{branchName}</div>
          <div className="text-[11px] text-stone-500">{b.phone || "—"} {b.area_label ? `· ${b.area_label}` : ""}</div>
        </div>
        {b.geo && !edit ? (
          <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 size={10} /> إحداثيات
          </span>
        ) : null}
        <button data-testid={`branch-geo-edit-${idx}`} className="text-[11px] text-[#7A1F3D] hover:underline" onClick={() => setEdit(!edit)}>
          {edit ? "إلغاء" : (b.geo ? "تعديل" : "تحديد")}
        </button>
      </div>

      {/* Marketplace visibility toggle — independent from geo edit */}
      <div className="flex items-center justify-between gap-3 p-2 rounded-md bg-white border border-stone-200" data-testid={`branch-mp-toggle-row-${idx}`}>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-stone-800 flex items-center gap-1.5">
            {inMp
              ? <><Eye size={12} className="text-emerald-600" /> معروض في الماركت بليس</>
              : <><EyeOff size={12} className="text-stone-400" /> مخفي من الماركت بليس</>
            }
          </div>
          <div className="text-[10px] text-stone-500 mt-0.5">
            {inMp ? "العملاء يرون هذا الفرع ويستطيعون الحجز فيه" : "لن يظهر هذا الفرع للعملاء على المنصة"}
          </div>
        </div>
        <button
          type="button"
          onClick={() => toggleMarketplace(!inMp)}
          disabled={busy}
          data-testid={`branch-mp-toggle-${idx}`}
          aria-pressed={inMp}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition disabled:opacity-50 ${inMp ? "bg-[#7A1F3D]" : "bg-stone-300"}`}
        >
          <span className={`inline-block h-4 w-4 bg-white rounded-full shadow transform transition ${inMp ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>

      {edit && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-[12px]">
          <div>
            <label className="block text-[10px] text-stone-500 mb-0.5">خط العرض (Lat)</label>
            <input data-testid={`branch-geo-lat-${idx}`} value={lat} onChange={(e) => setLat(e.target.value)} placeholder="25.286" className="w-full px-2 py-1.5 rounded border border-stone-200" dir="ltr" />
          </div>
          <div>
            <label className="block text-[10px] text-stone-500 mb-0.5">خط الطول (Lng)</label>
            <input data-testid={`branch-geo-lng-${idx}`} value={lng} onChange={(e) => setLng(e.target.value)} placeholder="51.531" className="w-full px-2 py-1.5 rounded border border-stone-200" dir="ltr" />
          </div>
          <div className="col-span-2 flex items-center gap-2 pt-1">
            <button data-testid={`branch-geo-locate-${idx}`} disabled={busy} type="button" onClick={useMyLocation} className="text-[11px] px-2 py-1 rounded border border-stone-200 hover:border-[#7A1F3D]">
              📍 استخدم موقعي الحالي
            </button>
            <button data-testid={`branch-geo-save-${idx}`} disabled={busy} type="button" onClick={save} className="text-[11px] px-3 py-1 rounded bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white">
              حفظ
            </button>
            {lat && lng && (
              <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#7A1F3D] hover:underline ms-auto">
                معاينة على خرائط جوجل ↗
              </a>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

// ---------- Media (cover / logo / gallery) ----------
function MediaSection({ listing, onSaved }) {
  const media = listing.media || {};
  const [gallery, setGallery] = useState(Array.isArray(media.gallery) ? media.gallery : []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setGallery(Array.isArray(listing.media?.gallery) ? listing.media.gallery : []);
  }, [listing.media?.gallery]);

  const resolveUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    const base = API_BASE.replace(/\/api$/, "");
    return base + u;
  };

  const saveCover = async (newUrl) => {
    try {
      setBusy(true);
      await api.put("/marketplace/listing/me/media", { cover: newUrl || null });
      toast.success(newUrl ? "تم حفظ صورة الغلاف ✓" : "تم حذف صورة الغلاف");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setBusy(false); }
  };

  const saveLogo = async (newUrl) => {
    try {
      setBusy(true);
      await api.put("/marketplace/listing/me/media", { logo: newUrl || null });
      toast.success(newUrl ? "تم حفظ الشعار ✓" : "تم حذف الشعار");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setBusy(false); }
  };

  const addGalleryItem = async (newUrl) => {
    if (!newUrl) return;
    const next = [...gallery, newUrl];
    setGallery(next);
    try {
      setBusy(true);
      await api.put("/marketplace/listing/me/media", { gallery: next });
      toast.success("تمت إضافة الصورة للمعرض ✓");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
      setGallery(gallery);
    } finally { setBusy(false); }
  };

  const removeGalleryItem = async (idx) => {
    const next = gallery.filter((_, i) => i !== idx);
    setGallery(next);
    try {
      setBusy(true);
      await api.put("/marketplace/listing/me/media", { gallery: next });
      toast.success("تمت إزالة الصورة");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
      setGallery(gallery);
    } finally { setBusy(false); }
  };

  const venueName = (listing.name?.ar) || listing.name?.en || "صالونك";
  const cover = listing.media?.cover || "";
  const logo = listing.media?.logo || "";

  return (
    <div className="space-y-4 mt-4" data-testid="marketplace-media-section">
      <div className="flex items-center gap-2 px-1">
        <ImageIcon size={18} className="text-[#7A1F3D]" />
        <h2 className="text-base font-bold text-stone-800">صور الصالون التي يراها العملاء</h2>
        <span className="text-xs text-stone-500">— تظهر في الماركت بليس العام لتُغري الزبائن بالحجز</span>
      </div>

      {/* ============ صورة الغلاف (Hero) ============ */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="mp-media-cover-card">
        <div className="px-5 py-4 border-b border-stone-100 bg-gradient-to-l from-stone-50 to-white">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-bold text-stone-800 flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-[#7A1F3D] text-white text-xs flex items-center justify-center font-bold">1</span>
                صورة الغلاف الرئيسية
              </h3>
              <p className="text-xs text-stone-600 mt-1.5 leading-relaxed">
                هذه أول صورة يراها العميل عند فتح صفحة صالونك في الماركت بليس. تأخذ كامل عرض الشاشة في الأعلى.
                <br />
                <span className="text-stone-400">المقاس المثالي: <span className="font-mono">1920×820</span> بكسل · صيغة JPG أو PNG</span>
              </p>
            </div>
            {cover && (
              <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1 shrink-0">
                <CheckCircle2 size={11} /> تم الرفع
              </span>
            )}
          </div>
        </div>
        <div className="p-5">
          <ImageUpload
            value={cover}
            onChange={saveCover}
            category="marketplace_cover"
            aspect="aspect-[21/9]"
            recommendedSize="1920×820"
            testIdPrefix="mp-listing-cover"
            lang="ar"
          />
        </div>
      </div>

      {/* ============ شعار الصالون (Logo) ============ */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="mp-media-logo-card">
        <div className="px-5 py-4 border-b border-stone-100 bg-gradient-to-l from-stone-50 to-white">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-bold text-stone-800 flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-[#7A1F3D] text-white text-xs flex items-center justify-center font-bold">2</span>
                شعار الصالون (الـلوجو)
              </h3>
              <p className="text-xs text-stone-600 mt-1.5 leading-relaxed">
                الشعار الذي يميّز صالونك. يظهر في 3 أماكن:
                <span className="font-semibold text-stone-700"> بطاقة الصالون في الصفحة الرئيسية </span>·
                <span className="font-semibold text-stone-700"> أعلى صفحة الصالون </span>·
                <span className="font-semibold text-stone-700"> نتائج البحث</span>.
                <br />
                <span className="text-stone-400">المقاس المثالي: <span className="font-mono">512×512</span> بكسل (مربع) · يفضّل صيغة PNG بخلفية شفافة</span>
              </p>
            </div>
            {logo && (
              <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1 shrink-0">
                <CheckCircle2 size={11} /> تم الرفع
              </span>
            )}
          </div>
        </div>
        <div className="p-5 grid sm:grid-cols-[200px_1fr] gap-5">
          <div className="max-w-[200px]">
            <ImageUpload
              value={logo}
              onChange={saveLogo}
              category="marketplace_logo"
              aspect="aspect-square"
              recommendedSize="512×512"
              testIdPrefix="mp-listing-logo"
              lang="ar"
            />
          </div>
          <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
            <div className="text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-3">معاينة الشعار على بطاقة الصالون</div>
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm max-w-[280px]">
              <div className="aspect-[4/3] bg-gradient-to-br from-stone-100 to-stone-200 relative">
                {cover ? <img src={resolveUrl(cover)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-stone-300 text-3xl">🪞</div>}
                {logo && (
                  <div className="absolute bottom-2 start-2 w-12 h-12 rounded-xl bg-white shadow-md border-2 border-white overflow-hidden">
                    <img src={resolveUrl(logo)} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="font-bold text-sm text-stone-800 truncate">{venueName}</div>
                <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-1">⭐ 0.0 (0)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ معرض الصور (Gallery) ============ */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="mp-media-gallery-card">
        <div className="px-5 py-4 border-b border-stone-100 bg-gradient-to-l from-stone-50 to-white">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-bold text-stone-800 flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-[#7A1F3D] text-white text-xs flex items-center justify-center font-bold">3</span>
                معرض صور الصالون
                <span className="text-xs font-normal text-stone-400 ms-1">({gallery.length}/20)</span>
              </h3>
              <p className="text-xs text-stone-600 mt-1.5 leading-relaxed">
                صور تظهر للعميل عندما يضغط على تبويب «الصور» في صفحة صالونك.
                يُنصح بعرض: <span className="font-semibold text-stone-700">واجهة الصالون · المقاعد · غرف العلاج · الأجواء · أعمال سابقة</span>.
                <br />
                <span className="text-stone-400">المقاس المثالي: <span className="font-mono">1200×1200</span> بكسل أو أكبر · حتى 20 صورة كحد أقصى</span>
              </p>
            </div>
          </div>
        </div>
        <div className="p-5">
          {gallery.length === 0 ? (
            <div className="aspect-[16/6] border-2 border-dashed border-stone-200 rounded-xl flex flex-col items-center justify-center text-stone-400 text-center px-4 mb-3">
              <ImageIcon size={32} className="opacity-50 mb-2" />
              <div className="text-sm font-semibold text-stone-600">لا توجد صور في المعرض بعد</div>
              <div className="text-xs mt-1">ابدأ بإضافة أول صورة من الأسفل ⬇</div>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mb-3">
              {gallery.map((url, i) => (
                <div key={`${url}-${i}`} className="relative aspect-square rounded-lg overflow-hidden border border-stone-200 group" data-testid={`mp-listing-gallery-item-${i}`}>
                  <img src={resolveUrl(url)} alt={`gallery ${i+1}`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition" />
                  <button
                    type="button"
                    onClick={() => removeGalleryItem(i)}
                    disabled={busy}
                    data-testid={`mp-listing-gallery-remove-${i}`}
                    className="absolute top-1.5 end-1.5 w-7 h-7 rounded-full bg-black/70 text-white hover:bg-rose-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition shadow-lg"
                    title="إزالة"
                  >
                    <XIcon size={13} />
                  </button>
                  <div className="absolute bottom-1 start-1 text-[10px] text-white font-bold opacity-0 group-hover:opacity-100 transition">#{i+1}</div>
                </div>
              ))}
            </div>
          )}
          {gallery.length < 20 ? (
            <div>
              <div className="text-[11px] font-bold text-stone-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Plus size={12} /> إضافة صورة جديدة للمعرض
              </div>
              <ImageUpload
                value=""
                onChange={addGalleryItem}
                category="marketplace_gallery"
                aspect="aspect-square"
                recommendedSize="1200×1200"
                testIdPrefix="mp-listing-gallery-add"
                lang="ar"
              />
            </div>
          ) : (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
              وصلت للحد الأقصى (20 صورة). احذف صورة قديمة لإضافة جديدة.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
